import { HttpClient } from '../../../services/api/client'
import { deduplicateRequest } from '../../../services/api/deduplicateRequest'
import { TokenManager } from '../../../services/auth/tokenManager'
import type {
  ApiResponse,
  InterviewQuestion,
  InterviewSaveNotesResponse,
  InterviewSimulateResponse,
  InterviewStatsResponse,
  InterviewNotesResponse,
} from '@bandos/shared'

const dedupedGetStats = deduplicateRequest(async (nodeId: string) => {
  const headers = await TokenManager.getAuthHeaders()
  return HttpClient.get<InterviewStatsResponse>(`/interview/${nodeId}/stats`, headers)
})

export class InterviewService {
  /**
   * Fetch interview stats for a node (authenticated — creator only).
   * Deduplicates concurrent requests for the same nodeId within a 2s window.
   */
  static getStats(nodeId: string): Promise<ApiResponse<InterviewStatsResponse>> {
    return dedupedGetStats(nodeId)
  }

  /**
   * Fetch all interview notes for a node (authenticated — creator only).
   */
  static async getNotes(nodeId: string): Promise<ApiResponse<InterviewNotesResponse>> {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.get<InterviewNotesResponse>(`/interview/${nodeId}/notes`, headers)
  }

  /**
   * Fetch interview questions and parent label for a node (authenticated — creator only).
   */
  static async getInterviewData(nodeId: string): Promise<
    ApiResponse<{
      parentLabel: string
      interviewQuestions: InterviewQuestion[]
      interviewCreatedAt: string | null
    }>
  > {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.get<{
      parentLabel: string
      interviewQuestions: InterviewQuestion[]
      interviewCreatedAt: string | null
    }>(`/interview/${nodeId}`, headers)
  }

  /**
   * Save interview notes (authenticated — creator only).
   */
  static async saveNotes(
    nodeId: string,
    answers: (string | null)[],
    durationSeconds?: number,
    intervieweeName?: string
  ): Promise<ApiResponse<InterviewSaveNotesResponse>> {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.post<InterviewSaveNotesResponse>(
      `/interview/${nodeId}/notes`,
      { answers, durationSeconds, intervieweeName },
      headers
    )
  }

  /**
   * Generate simulated human answers for all questions in an interview (for testing).
   */
  static async simulate(nodeId: string): Promise<ApiResponse<InterviewSimulateResponse>> {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.post<InterviewSimulateResponse>(`/interview/${nodeId}/simulate`, {}, headers)
  }
}
